import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import Navigation from '../components/Navigation';
import MimoCharacter from '../components/MimoCharacter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Chat = () => {
  const { user, getAgeConfig } = useUser();
  const config = getAgeConfig();
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/history`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    // Optimistically add user message
    const tempUserMsg = {
      message_id: `temp_${Date.now()}`,
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: userMessage },
        { withCredentials: true }
      );
      
      // Replace temp message with actual and add assistant response
      setMessages(prev => {
        const filtered = prev.filter(m => m.message_id !== tempUserMsg.message_id);
        return [...filtered, response.data.user_message, response.data.assistant_message];
      });
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m.message_id !== tempUserMsg.message_id));
    } finally {
      setLoading(false);
    }
  };

  const getWelcomeMessage = () => {
    const greetings = {
      explorador: '¡Hola! Soy MIMO, tu amigo mágico. ¿Qué aventura tenemos hoy? 🌟',
      companero: '¡Hey! Soy MIMO. Estoy aquí para escucharte. ¿Qué tal tu día?',
      aliado: 'Hola. Soy MIMO, tu aliado personal. ¿En qué puedo ayudarte hoy?'
    };
    return greetings[user?.age_mode] || greetings.aliado;
  };

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-8 lg:pl-20 flex flex-col">
      <Navigation />
      
      <main className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-4 sm:px-6 py-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 pb-4 border-b border-border mb-4"
        >
          <MimoCharacter mood="happy" size="sm" />
          <div>
            <h1 className={`${config.fontClass} text-xl font-bold text-foreground`}>
              Chat con MIMO
            </h1>
            <p className="text-sm text-muted-foreground">Tu compañero siempre disponible</p>
          </div>
        </motion.div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {initialLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-[#FF7A59]" />
            </div>
          ) : messages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center h-full text-center"
            >
              <MimoCharacter mood="happy" size="lg" />
              <p className="mt-6 text-lg text-foreground max-w-sm">
                {getWelcomeMessage()}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Escríbeme lo que quieras
              </p>
            </motion.div>
          ) : (
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={message.message_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-8 h-8 mr-2 flex-shrink-0">
                      <MimoCharacter mood="happy" size="sm" showAura={false} />
                    </div>
                  )}
                  
                  <div
                    className={`max-w-[80%] px-4 py-3 ${config.cardRadius} ${
                      message.role === 'user'
                        ? 'bg-[#FF7A59] text-white'
                        : 'glass'
                    }`}
                    data-testid={`message-${message.role}`}
                  >
                    <p className={`text-sm ${message.role === 'user' ? 'text-white' : 'text-foreground'}`}>
                      {message.content}
                    </p>
                  </div>
                </motion.div>
              ))}
              
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="w-8 h-8 mr-2 flex-shrink-0">
                    <MimoCharacter mood="calm" size="sm" showAura={false} />
                  </div>
                  <div className={`px-4 py-3 ${config.cardRadius} glass`}>
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={sendMessage}
          className="flex gap-2 pt-4 border-t border-border"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe un mensaje..."
            className={`flex-1 ${config.buttonRadius}`}
            disabled={loading}
            data-testid="chat-input"
          />
          <Button
            type="submit"
            disabled={loading || !input.trim()}
            className={`bg-[#FF7A59] hover:bg-[#E66A4B] text-white ${config.buttonRadius}`}
            data-testid="chat-send-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </motion.form>
      </main>
    </div>
  );
};

export default Chat;
